import React from 'react';
import PropTypes from 'prop-types';
import Router from 'next/router';
import Alert from '@illustrations/Alert';
import useMeQuery from '@hooks/useMeQuery';
import { useTrackPageLoad } from '@cvent/sli-nextjs-metrics';
import httpLogPageLoadId from '@tools/httpLogPageLoadId';
import { IllustrationErrorTypes as ErrorTypes } from '@cvent/carina/components/Templates/IllustrationTypes';
import { DockError } from '@components/shared/ComposedError';
import useAppStats from '@hooks/useAppStats';

/**
 * This component shows the error page, and is for use by the ErrorBoundary
 * component. It is shown for client errors that occur within a page or it's
 * children. For client errors that occur at the app level see `src/pages/_error.js`.
 * Additionally see `src/metrics/ErrorBoundary.js` for more information.
 */
const Error = ({ reset, httpLogRequestId }) => {
  return (
    <DockError
      errorType={ErrorTypes.PAGE_NOT_FOUND}
      httpLogPageLoadId={httpLogPageLoadId}
      httpLogRequestId={httpLogRequestId}
      customImage={<Alert />}
      customActions={[
        {
          value: 'Go Back',
          onClick: () => {
            Router.back();
            // Once we go back, we need to call reset in order for the ErrorBoundary
            // to start rendering again.
            reset();
          }
        }
      ]}
    />
  );
};

Error.propTypes = {
  // Reset is a function to call to reset the error state to attempt to render content
  // again.
  reset: PropTypes.func.isRequired,
  // requestId for this page load
  httpLogRequestId: PropTypes.string
};

/**
 * Error Delegation component. We want errors to be unique per attendee so we need
 * to pass the attendeeId when we track errors
 *
 * @param {*} param0
 * @returns
 */
export const SliErrorBoundary = ({ reset, error, sliData = {} }) => {
  const { data: { me: { id: attendeeId } = {} } = {} } = useMeQuery();

  const loading = false;
  const err = { message: error?.message || 'Unhandled Error' };
  const { httpLogRequestId } = useAppStats();
  useTrackPageLoad(loading, err, false, {
    error_type: 'unhandled_error',
    attendeeId,
    httpLogRequestId,
    ...sliData
  });

  return <Error reset={reset} httpLogRequestId={sliData?.httpLogRequestId} />;
};

SliErrorBoundary.propTypes = {
  // function to reset the error state to attempt to render content again.
  reset: PropTypes.func.isRequired,
  // The url where the error was caught
  url: PropTypes.string.isRequired,
  // The error caught by the metrics error boundary
  error: PropTypes.object.isRequired,
  // The sliData that will be sent with the error
  sliData: PropTypes.object
};

export default Error;
